// src/MathTools/CoordinatePlane.js

import { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

export default function CoordinatePlane({
  width = 400,
  height = 400,
  showGrid = false,
  showAxes = false,
  lines = [],
  disablePlot = false,
  xDomain = [-10, 10],
  yDomain = [-10, 10],
  axisMode = "origin",

  // Optional props for custom tick arrays
  xTickValues = null,
  yTickValues = null,

  // NEW optional array of points to highlight (does not interfere with other slides)
  highlightPoints = []
}) {
  const svgRef = useRef(null);
  // If user-clicking is enabled, we store newly clicked points
  const [points, setPoints] = useState([]);

  // Build scale functions
  const xScale = d3.scaleLinear().domain(xDomain).range([0, width]);
  const yScale = d3.scaleLinear().domain(yDomain).range([height, 0]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);

    // Clear old drawings
    svg.selectAll("*").remove();

    // 1) Possibly draw grid lines
    if (showGrid) {
      // vertical grid lines for xDomain[0]..[1]
      svg.append("g")
        .selectAll("line.xgrid")
        .data(d3.range(xDomain[0], xDomain[1] + 1))
        .enter()
        .append("line")
        .attr("class", "xgrid")
        .attr("x1", d => xScale(d))
        .attr("y1", 0)
        .attr("x2", d => xScale(d))
        .attr("y2", height)
        .attr("stroke", "#eee");

      // horizontal grid lines for yDomain[0]..[1]
      svg.append("g")
        .selectAll("line.ygrid")
        .data(d3.range(yDomain[0], yDomain[1] + 1))
        .enter()
        .append("line")
        .attr("class", "ygrid")
        .attr("x1", 0)
        .attr("y1", d => yScale(d))
        .attr("x2", width)
        .attr("y2", d => yScale(d))
        .attr("stroke", "#eee");
    }

    // 2) Possibly draw axes
    if (showAxes) {
      if (axisMode === "border") {
        // Axes at bottom and left edges
        const xAxis = d3.axisBottom(xScale);
        // if xTickValues provided, use them:
        if (xTickValues) {
          xAxis.tickValues(xTickValues);
        } else {
          xAxis.ticks(10); // fallback if none provided
        }
        svg.append("g")
          .attr("transform", `translate(0, ${height})`)
          .call(xAxis);

        const yAxis = d3.axisLeft(yScale);
        if (yTickValues) {
          yAxis.tickValues(yTickValues);
        } else {
          yAxis.ticks(10);
        }
        svg.append("g")
          .attr("transform", "translate(0,0)")
          .call(yAxis);

      } else {
        // axisMode="origin" => x-axis at y=0, y-axis at x=0
        const xAxis = d3.axisBottom(xScale);
        if (xTickValues) {
          xAxis.tickValues(xTickValues);
        } else {
          // your old default in "origin" mode
          xAxis.tickValues(d3.range(xDomain[0], xDomain[1] + 1, 2));
        }
        svg.append("g")
          .attr("transform", `translate(0, ${yScale(0)})`)
          .call(xAxis);

        const yAxis = d3.axisLeft(yScale);
        if (yTickValues) {
          yAxis.tickValues(yTickValues);
        } else {
          yAxis.tickValues(d3.range(yDomain[0], yDomain[1] + 1, 2));
        }
        svg.append("g")
          .attr("transform", `translate(${xScale(0)}, 0)`)
          .call(yAxis);
      }
    }

    // 3) Draw the lines (or single‐point circles)
    lines.forEach(({ start, end, color = "black", label }) => {
      const [x1, y1] = start;
      const [x2, y2] = end;

      if (x1 === x2 && y1 === y2) {
        // single point => draw circle
        svg.append("circle")
          .attr("cx", xScale(x1))
          .attr("cy", yScale(y1))
          .attr("r", 5)
          .attr("fill", color);

        if (label) {
          svg.append("text")
            .attr("x", xScale(x1) + 6)
            .attr("y", yScale(y1) - 6)
            .attr("fill", color)
            .attr("font-size", 16)
            .text(label);
        }
      } else {
        // line from (x1, y1) to (x2, y2)
        svg.append("line")
          .attr("x1", xScale(x1))
          .attr("y1", yScale(y1))
          .attr("x2", xScale(x2))
          .attr("y2", yScale(y2))
          .attr("stroke", color)
          .attr("stroke-width", 2);

        // optional midpoint label
        if (label) {
          const mx = (x1 + x2) / 2;
          const my = (y1 + y2) / 2;
          svg.append("text")
            .attr("x", xScale(mx))
            .attr("y", yScale(my) - 5)
            .attr("fill", color)
            .attr("font-size", 12)
            .text(label);
        }
      }
    });

    // 4) If plotting is not disabled, let user add red points by click
    if (!disablePlot) {
      // draw existing user points
      svg.selectAll(".point")
        .data(points)
        .enter()
        .append("circle")
        .attr("class", "point")
        .attr("cx", d => xScale(d.x))
        .attr("cy", d => yScale(d.y))
        .attr("r", 4)
        .attr("fill", "red");

      // invisible rect to handle clicks
      svg.append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "transparent")
        .on("click", event => {
          const [px, py] = d3.pointer(event);
          const xVal = xScale.invert(px);
          const yVal = yScale.invert(py);
          const xRounded = Number(xVal.toFixed(1));
          const yRounded = Number(yVal.toFixed(1));
          setPoints(curr => [...curr, { x: xRounded, y: yRounded }]);
        });
    }

    // 5) NEW: Draw highlightPoints if any are passed
    if (highlightPoints.length > 0) {
      highlightPoints.forEach(({ x, y, label, color = "black" }) => {
        // Draw the point
        svg.append("circle")
          .attr("cx", xScale(x))
          .attr("cy", yScale(y))
          .attr("r", 5)
          .attr("fill", color);

        // If a label is provided, place it near the point
        if (label) {
          svg.append("text")
            .attr("x", xScale(x) + 6)
            .attr("y", yScale(y) - 6)
            .attr("fill", color)
            .attr("font-size", 14)
            .text(label);
        }
      });
    }
    // END new code
  }, [
    lines, points, showGrid, showAxes, disablePlot,
    xDomain, yDomain, width, height, axisMode,
    xTickValues, yTickValues,
    highlightPoints,
    xScale, yScale
  ]);

  return (
    <div style={{ border: "1px solid #ccc", display: "inline-block" }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ display: "block" }}
      />
      {/* If user can plot points, show them below */}
      {!disablePlot && (
        <div style={{ padding: 5 }}>
          <strong>Plotted Points:</strong>
          <ul style={{ marginTop: 5 }}>
            {points.map((pt, i) => (
              <li key={i}>
                ( {pt.x}, {pt.y} )
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
